import React, { FC, memo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ErrorMessage } from './error-message';

const inputMixin = css`
  width: 100%;
  display: block;
  caret-color: ${p => p.theme.colors.red};
  background-color: transparent;
  outline: none;
  appearance: none;
  pointer-events: unset;
  font-size: 1.125em;
  line-height: 1.5;
  color: #000;
  font-weight: 400;

  &::placeholder {
    color: ${p => p.theme.colors.gray2};
  }

  &:disabled {
    background-color: ${p => p.theme.colors.blueMid};
    border-color: ${p => p.theme.colors.blueMid};
    cursor: not-allowed;
  }
`;

const InputContainer = styled.div<{ isFocused: boolean; isError: boolean }>`
  position: relative;
  width: 100%;
  margin-bottom: 2em;

  &::before {
    left: 0;
    right: 0;
    bottom: 0;
    content: '\\00a0';
    position: absolute;
    transition: border-width 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.7);
    pointer-events: none;
    ${p =>
      p.isError &&
      css`
        border-color: ${p.theme.colors.red};
      `}
  }

  &::after {
    left: 0;
    right: 0;
    bottom: 0;
    content: '';
    position: absolute;
    transform: ${p => (p.isFocused ? `scaleX(1)` : `scaleX(0)`)};
    transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    border-bottom: 2px solid ${p => p.theme.colors.blueLight};
    pointer-events: none;
    ${p =>
      p.isError &&
      css`
        border-color: ${p.theme.colors.red};
      `}
  }

  &:hover {
    &::before {
      border-width: 2px;
    }
  }

  > input {
    padding: 0.7em 0.2em;
    ${inputMixin}
    ${p =>
      p.isError &&
      css`
        color: ${p.theme.colors.red};
      `}
  }
`;

type InputProps = {
  name: string;
  type?: string;
  label: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
};

export const Input: FC<InputProps> = memo(
  ({
    type = '',
    name,
    label,
    placeholder = '',
    autoComplete = 'off',
    required = false,
    disabled = false,
  }) => {
    const { register, errors } = useFormContext();
    const [isFocused, setIsFocused] = useState(false);

    return (
      <InputContainer isFocused={isFocused} isError={errors[name]}>
        <label hidden htmlFor={name}>
          {label}
        </label>
        <input
          id={name}
          aria-label={name}
          type={type}
          name={name}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          ref={register({ required })}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <ErrorMessage name={name} />
      </InputContainer>
    );
  }
);

const TextAreaContainer = styled.div<{ isFocused: boolean }>`
  position: relative;
  width: 100%;
  margin-bottom: 3em;
  padding: 0.7em;

  &::before {
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    content: '';
    position: absolute;
    transition: box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    box-shadow: ${p =>
      p.isFocused
        ? `0 0 0 2px ${p.theme.colors.blueLight}`
        : `0 0 0 0.5px rgba(0, 0, 0, 0.7)`};
    pointer-events: none;
  }

  &:hover {
    &::before {
      box-shadow: ${p => !p.isFocused && `0 0 0 2px rgba(0, 0, 0, 0.7)`};
    }
  }

  > textarea {
    height: auto;
    resize: vertical;
    padding-right: 0.5em;
    ${inputMixin}
  }
`;

const TextareaLabel = styled.div`
  display: inline-block;
  font-size: 1.125em;
  line-height: 1.5;
  margin-bottom: 0.7em;
  color: ${p => p.theme.colors.gray2};
`;

type TextareaProps = {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number;
};

export const Textarea: FC<TextareaProps> = memo(
  ({
    name,
    label,
    placeholder = '',
    required = false,
    disabled = false,
    rows = 10,
  }) => {
    const { register } = useFormContext();

    const [isFocused, setIsFocused] = useState(false);

    return (
      <div>
        <TextareaLabel>{label}</TextareaLabel>
        <TextAreaContainer isFocused={isFocused}>
          <label hidden htmlFor={name}>
            {label}
          </label>
          <textarea
            id={name}
            aria-label={name}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            ref={register({ required })}
            rows={rows}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </TextAreaContainer>
      </div>
    );
  }
);
